import { cn } from "cn";
import { Check } from "lucide-react";
import * as React from "react";

// v3.2 generation progress: what ROSC is doing while a document is written
// (a note, a referral letter, a patient summary, a template rewrite). A title
// and, under it, the steps: done ones checked, the current one turning, the
// rest waiting. The last step never shows as done: the caller removes the
// progress when the result arrives, so it never claims to be finished early.

export interface GenerationStep {
  /** Stable key (also data-step on the row). */
  key: string;
  /** "Reading the transcript…", already localized. */
  label: React.ReactNode;
}

export type GenerationStepState = "done" | "active" | "waiting";

/** Done before the current step, active at it, waiting after it. */
export const generationStepState = (
  index: number,
  current: number,
): GenerationStepState =>
  index < current ? "done" : index === current ? "active" : "waiting";

const StepMark = ({
  state,
}: {
  state: GenerationStepState;
}): React.JSX.Element => (
  <span
    aria-hidden
    className={cn(
      "flex size-[18px] shrink-0 items-center justify-center rounded-full",
      state === "done" && "bg-v3-brand text-white",
      state === "active" &&
        "animate-spin border-2 border-v3-brand border-t-transparent [animation-duration:900ms] motion-reduce:animate-none motion-reduce:border-t-v3-brand",
      state === "waiting" && "shadow-[inset_0_0_0_1.5px_var(--v3-text-3)]",
    )}
  >
    {state === "done" && <Check className="size-3" strokeWidth={3.5} />}
  </span>
);

/**
 * Progress while a document is written. `current` is the index of the step
 * under way (clamped to the steps); every step before it shows as done.
 * `plain` drops the tinted block (inside a sheet or a conversation).
 */
export function GenerationProgress({
  title,
  description,
  steps,
  current,
  plain = false,
  className,
  testId,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  steps: readonly GenerationStep[];
  current: number;
  plain?: boolean;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  const at = Math.max(0, Math.min(current, steps.length - 1));
  return (
    <div
      aria-busy
      className={cn(
        "font-v3 text-v3-text",
        !plain && "rounded-[14px] bg-v3-brand-tint p-4",
        className,
      )}
      data-current={steps[at]?.key}
      data-slot="generation-progress"
      data-testid={testId}
      role="status"
    >
      <p
        className={cn(
          "text-[14px] font-semibold",
          plain ? "text-v3-text" : "text-v3-brand-strong",
        )}
      >
        {title}
      </p>
      {description && (
        <p className="mt-0.5 text-[13.5px] leading-[1.45] text-v3-text-2">
          {description}
        </p>
      )}
      <ol className="mt-3 flex flex-col gap-2.5">
        {steps.map((step, index) => {
          const state = generationStepState(index, at);
          return (
            <li
              key={step.key}
              aria-current={state === "active" ? "step" : undefined}
              className={cn(
                "flex min-w-0 items-center gap-2.5 text-[13.5px] leading-5 transition-colors",
                state === "done" && "text-v3-text-2",
                state === "active" && "font-medium text-v3-text",
                state === "waiting" && "text-v3-text-3",
              )}
              data-state={state}
              data-step={step.key}
            >
              <StepMark state={state} />
              <span className="min-w-0">{step.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Time-based steps for work that reports no stages of its own: 0 until the
 * first delay (ms since `running` turned on), then 1, and so on. It stops at
 * `delays.length`, so the caller's last step stays current until the result
 * arrives. Starts again from 0 each time `running` turns on or `key` changes.
 */
export function useTimedStep(
  delays: readonly number[],
  running = true,
  key?: unknown,
): number {
  const [step, setStep] = React.useState(0);
  const schedule = delays.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: restarts on a new run or key only.
  React.useEffect(() => {
    setStep(0);
    if (!running) return;
    const timers = schedule
      .split(",")
      .filter(Boolean)
      .map((delay, index) =>
        setTimeout(() => setStep(index + 1), Number(delay)),
      );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [running, key, schedule]);
  return step;
}
